import React from "react";
import { Stack, Typography } from "@mui/material";
import type { TypographyProps } from "@mui/material";

import { fmtDateShort } from "../../utils/format";

interface CampaignDateRangeProps {
  /** campaignsStartDate del briefing (ISO date-time o null) */
  startDate?: string | null;
  /** campaignsEndDate del briefing (ISO date-time o null) */
  endDate?: string | null;
  /** Mes del briefing (1-12), fallback cuando no hay rango */
  month?: number | null;
  /** Año del briefing, fallback cuando no hay rango */
  year?: number | null;
  /** Variante de Typography para el texto principal */
  variant?: TypographyProps["variant"];
  /**
   * "vertical" (por defecto): rango apilado con el mes/año como subtítulo.
   * "horizontal": mes/año a la izquierda y el rango inicio/fin a su derecha
   * (aprovecha el espacio horizontal, p.ej. en la tabla de briefings).
   */
  layout?: "vertical" | "horizontal";
}

/** Mes/año del briefing como "MM/YYYY", o "-" si falta. */
const formatMonthYear = (month?: number | null, year?: number | null) => {
  if (!month || !year) return "-";
  return `${String(month).padStart(2, "0")}/${year}`;
};

/**
 * Muestra el rango de fechas de las campañas asociadas del briefing.
 *
 * - Si hay startDate y/o endDate → se muestra el rango inicio/fin junto al
 *   mes/año del briefing (apilado o en horizontal según `layout`).
 * - Si ambos son null → fallback al mes/año del briefing (como antes).
 */
const CampaignDateRange = ({
  startDate,
  endDate,
  month,
  year,
  variant = "body2",
  layout = "vertical",
}: CampaignDateRangeProps) => {
  const start = fmtDateShort(startDate);
  const end = fmtDateShort(endDate);
  const monthYear = formatMonthYear(month, year);
  const bold = variant !== "body2";

  if (!start && !end) {
    return (
      <Typography variant={variant} fontWeight={bold ? 700 : 400}>
        {monthYear}
      </Typography>
    );
  }

  // Rango inicio/fin apilado (inicio arriba, fin abajo).
  const range = (
    <Stack spacing={0}>
      {start && (
        <Typography variant="body2" color="text.secondary" lineHeight={1.2}>
          {start}
        </Typography>
      )}
      {end && (
        <Typography variant="body2" color="text.secondary" lineHeight={1.2}>
          {end}
        </Typography>
      )}
    </Stack>
  );

  if (layout === "horizontal") {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant={variant} fontWeight={bold ? 700 : 400} noWrap>
          {monthYear}
        </Typography>
        {range}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {start && (
        <Typography
          variant={variant}
          fontWeight={bold ? 700 : 400}
          lineHeight={1.2}
        >
          {start}
        </Typography>
      )}
      {end && (
        <Typography
          variant={variant}
          fontWeight={bold ? 700 : 400}
          lineHeight={1.2}
        >
          {end}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
        {monthYear}
      </Typography>
    </Stack>
  );
};

export default CampaignDateRange;
